import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import {
  addStatePolicyType,
  editStatePolicyType,
  rowEditPolicyType,
  savingPolicyType,
} from "../atoms/atoms";
import useUpdatePolicyType from "../hooks/useUpdatePolicyType";
import useAddPolicyType from "../hooks/useAddPolicyType";
import { policyTypeData } from "../types/PolicyTypeInterface";
import PolicyTypeActionDetails from "./PolicyTypeActionDetails";

export default function PolicyTypeActionForm() {
  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStatePolicyType);
  const [isEditState, setIsEditState] = useRecoilState(editStatePolicyType);
  const [isSaving, setIsSaving] = useRecoilState(savingPolicyType);
  const [editData, setEditData] = useRecoilState(rowEditPolicyType);

  // Local States
  const [newData, setNewData] = useState<policyTypeData>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // Hook Functions
  const updatePolicyType = useUpdatePolicyType();
  const addPolicyType = useAddPolicyType();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const policyTypeData = {
      ...data,
      policyTypeName: data.policyTypeName.trim(),
    };
    setNewData(policyTypeData);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  const handleSave = () => {
    if (newData) {
      if (isEditState) {
        updatePolicyType(newData);
      } else {
        addPolicyType(newData);
      }
    }
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Policy Type" : "Edit Policy Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <PolicyTypeActionDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Policy Type" : "Update Policy Type"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            setIsSaving(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
}
